
import {
  computed, defineComponent, onBeforeMount, reactive,
} from 'vue';
import { useAppBase } from '@/core/composables/AppBase';
import { ICliente, IClienteEndereco } from '@/models/Entidades/ICliente';
import { IPedido } from '@/models/Entidades/IPedido';
import ComunicacaoApi from '@/core/components/UI/ComunicacaoApi.vue';
import ServicoCliente from '@/servicos/ServicoCliente';
import ServicoUtilitario from '@/servicos/ServicoUtilitario';
import { IResposta } from '@/core/models/IResposta';
import ComboTipoPessoa from '@/components/ComboTipoPessoa.vue';
import ComboFinalidadeProdutos from '@/components/ComboFinalidadeProdutos.vue';
import CampoCep from '@/core/components/UI/CampoCep.vue';
import CampoCpf from '@/core/components/UI/CampoCpf.vue';
import CampoCnpj from '@/core/components/UI/CampoCnpj.vue';
import CampoTelefone from '@/core/components/UI/CampoTelefone.vue';
import ComboCidade from '@/components/ComboCidade.vue';
import ComboGenero from '@/components/ComboGenero.vue';
import SelecionarData from '@/core/components/UI/SelecionarData.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { ETipoPessoa } from '@/models/Enumeradores/ETipoPessoa';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import router from '@/router';
import { IDTOPedidosForcaVendasFiltros } from '@/models/DTO/ForcaVendas/IDTOPedidosForcaVendasFiltros';
import storeApp from '@/store/storeApp';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import ServicoPedido from '@/servicos/ServicoPedido';

type TTelaCliente = 'painel' | 'informacoes' | 'ultimasVendas' | 'editar';
type TAbaUltimasVendas = 'vendas' | 'itens';

interface IItemUltimasVendas {
  chave: string;
  codigo: number;
  codigoProduto: number;
  sku: string;
  produto: string;
  quantidade: number;
  precoUnitario: number;
  valorTotal: number;
}

export default defineComponent({
  name: 'ClienteCadastro',
  components: {
    ComunicacaoApi,
    ComboTipoPessoa,
    ComboFinalidadeProdutos,
    CampoCep,
    CampoCpf,
    CampoCnpj,
    CampoTelefone,
    ComboCidade,
    SelecionarData,
    ComboGenero,
  },
  setup() {
    const {
      appBase, apresentarComunicacaoApi, apresentarRespostaRedirecionamento, obterCodigoRota,
    } = useAppBase();

    const servicoCliente = new ServicoCliente();
    const servicoPedido = new ServicoPedido();
    const servicoUtilitario = new ServicoUtilitario();

    const state = reactive({
      cliente: {} as ICliente,
      pedidos: [] as IPedido[],
      pedidoSelecionadoIndex: -1,
      tela: 'painel' as TTelaCliente,
      abaUltimasVendas: 'vendas' as TAbaUltimasVendas,
      chaveEndereco: '0',
      cep: '',
      cpfCnpj: '',
      filtrosPedidos: {} as IDTOPedidosForcaVendasFiltros,
    });

    const tituloTela = computed(() => {
      if (state.tela === 'informacoes') return 'Informações do Cliente';
      if (state.tela === 'ultimasVendas') return 'Últimas Vendas';
      if (state.tela === 'editar') return 'Editar Cliente';

      return 'Cliente';
    });

    const nomeFantasiaCliente = computed(() => {
      if (UtilitarioGeral.valorValido(state.cliente.nomeFantasia)) {
        return state.cliente.nomeFantasia;
      }

      return state.cliente.nomeRazaoSocial || '-';
    });

    const descricaoCanalCliente = computed(() => {
      if (state.cliente.finalidadeProdutos > 0) {
        return 'DISTRIBUIDOR';
      }

      return '-';
    });

    const codigoClienteFormatado = computed(() => {
      if (state.cliente.codigo > 0) {
        return state.cliente.codigo.toString().padStart(8, '0');
      }

      return '00000000';
    });

    const cpfCnpjFormatado = computed(() => UtilitarioMascara.mascararCPFOuCNPJ(state.cliente.cpfCnpj));

    const enderecoPrincipal = computed(() => {
      if (UtilitarioGeral.validaLista(state.cliente.enderecos)) {
        const principal = state.cliente.enderecos.find((x) => x.principal);

        if (principal !== undefined) {
          return principal;
        }

        return state.cliente.enderecos[0];
      }

      return {} as IClienteEndereco;
    });

    const enderecoPrincipalCidade = computed(() => {
      if (enderecoPrincipal.value.codigoCidade > 0) {
        return `CIDADE ${enderecoPrincipal.value.codigoCidade}`;
      }

      return '';
    });

    const pedidoSelecionado = computed(() => {
      if (UtilitarioGeral.validaLista(state.pedidos) && state.pedidoSelecionadoIndex >= 0) {
        return state.pedidos[state.pedidoSelecionadoIndex];
      }

      return null;
    });

    const itensUltimasVendas = computed(() => {
      if (pedidoSelecionado.value !== null && UtilitarioGeral.validaLista(pedidoSelecionado.value.itens)) {
        return pedidoSelecionado.value.itens.map((item) => ({
          chave: `${item.codigo}-${item.codigoProduto}-${item.sku}`,
          codigo: item.codigo,
          codigoProduto: item.codigoProduto,
          sku: item.sku,
          produto: item.produto,
          quantidade: item.quantidade,
          precoUnitario: item.precoUnitario,
          valorTotal: item.valorTotal,
        } as IItemUltimasVendas));
      }

      const itensAgrupados = [] as IItemUltimasVendas[];

      state.pedidos.forEach((pedido) => {
        if (UtilitarioGeral.validaLista(pedido.itens)) {
          pedido.itens.forEach((item) => {
            const chave = `${item.codigoProduto}-${item.sku}-${item.produto}`;
            const itemAgrupado = itensAgrupados.find((x) => x.chave === chave);

            if (itemAgrupado !== undefined) {
              itemAgrupado.quantidade += item.quantidade;
              itemAgrupado.valorTotal += item.valorTotal;

              if (itemAgrupado.quantidade > 0) {
                itemAgrupado.precoUnitario = itemAgrupado.valorTotal / itemAgrupado.quantidade;
              }
            } else {
              itensAgrupados.push({
                chave,
                codigo: item.codigo,
                codigoProduto: item.codigoProduto,
                sku: item.sku,
                produto: item.produto,
                quantidade: item.quantidade,
                precoUnitario: item.precoUnitario,
                valorTotal: item.valorTotal,
              } as IItemUltimasVendas);
            }
          });
        }
      });

      return itensAgrupados.sort((a, b) => b.quantidade - a.quantidade);
    });

    const totalItensUltimasVendas = computed(() => itensUltimasVendas.value.reduce((total, item) => total + item.valorTotal, 0));

    function adicionarEndereco() {
      const endereco: IClienteEndereco = {} as IClienteEndereco;
      endereco.codigo = 0;
      endereco.principal = false;
      endereco.identificacao = '';
      endereco.destinatario = '';
      endereco.cep = '';
      endereco.numero = '';
      endereco.endereco = '';
      endereco.complemento = '';
      endereco.bairro = '';
      endereco.codigoCidade = 0;

      state.cliente.enderecos.push(endereco);
      state.chaveEndereco = (state.cliente.enderecos.length - 1).toString();
    }

    function removerEndereco(index: number) {
      state.cliente.enderecos.splice(index, 1);

      if (!UtilitarioGeral.validaLista(state.cliente.enderecos)) {
        adicionarEndereco();
        state.cliente.enderecos[0].principal = true;
      }
    }

    function limparDados() {
      state.cliente = {} as ICliente;
      state.cliente.codigo = 0;
      state.cliente.status = true;
      state.cliente.tipoPessoa = 1;
      state.cliente.finalidadeProdutos = 1;
      state.cliente.genero = 1;
      state.cliente.enderecos = [];
      state.pedidos = [];
      state.pedidoSelecionadoIndex = -1;
      state.tela = 'painel';
      state.abaUltimasVendas = 'vendas';

      adicionarEndereco();

      state.cliente.enderecos[0].principal = true;
      state.cpfCnpj = '';
      state.cep = '';
    }

    async function obterUltimasVendas(codigoCliente: number) {
      try {
        state.filtrosPedidos.codigoVendedor = storeApp.state.codigoVendedor;
        state.filtrosPedidos.codigosClientes = [];
        state.filtrosPedidos.codigosClientes.push(codigoCliente);
        state.filtrosPedidos.dataFinal = UtilitarioData.obterDataAtual();
        state.filtrosPedidos.dataInicial = UtilitarioData.diminuirDiaNaDataHoraMinutoSegundo(state.filtrosPedidos.dataFinal, 60);
        state.pedidos = await servicoPedido.pedidosForcaVendas(state.filtrosPedidos);
      } catch {
        state.pedidos = [];
      }
    }

    async function obterCliente(codigo: number) {
      appBase.carregando = true;

      state.cliente = await servicoCliente.obter(codigo);
      state.cpfCnpj = state.cliente.cpfCnpj;

      if (!UtilitarioGeral.validaLista(state.cliente.enderecos)) {
        state.cliente.enderecos = [];
        adicionarEndereco();
        state.cliente.enderecos[0].principal = true;
      }

      await obterUltimasVendas(codigo);

      appBase.carregando = false;
    }

    onBeforeMount(async () => {
      limparDados();

      const codigoCliente = obterCodigoRota();

      if (codigoCliente > 0) {
        await obterCliente(codigoCliente);
      }
    });

    async function salvar() {
      appBase.resposta = {} as IResposta;

      if (state.cliente.tipoPessoa === ETipoPessoa.Fisica) {
        state.cliente.identificacao = state.cliente.nomeRazaoSocial;
      }

      apresentarComunicacaoApi('Aguarde por favor... Estamos salvando as informações do Cliente.');

      if (state.cliente.codigo === 0) {
        appBase.resposta = await servicoCliente.incluir(state.cliente);
      } else {
        appBase.resposta = await servicoCliente.atualizar(state.cliente);
      }

      apresentarRespostaRedirecionamento('Clientes');
    }

    async function consultaCep(index: number) {
      if (
        UtilitarioGeral.valorValido(state.cliente.enderecos[index].cep)
        && UtilitarioGeral.validaValorComLimiteCaracteres(state.cliente.enderecos[index].cep, 8)
      ) {
        if (state.cliente.enderecos[index].cep !== state.cep) {
          const retornoConsultaCep = await servicoUtilitario.consultaCep(state.cliente.enderecos[index].cep);

          if (retornoConsultaCep !== undefined) {
            state.cliente.enderecos[index].endereco = retornoConsultaCep.logradouro;
            state.cliente.enderecos[index].complemento = retornoConsultaCep.complemento;
            state.cliente.enderecos[index].bairro = retornoConsultaCep.bairro;
            state.cliente.enderecos[index].codigoCidade = retornoConsultaCep.codigoCidade;
          }
        }
      }
    }

    async function consultaCnpj() {
      if (
        UtilitarioGeral.valorValido(state.cliente.cpfCnpj)
        && UtilitarioGeral.validaValorComLimiteCaracteres(state.cliente.cpfCnpj, 14)
      ) {
        if (state.cliente.cpfCnpj !== state.cpfCnpj) {
          const retornoConsultaCnpj = await servicoUtilitario.consultaCnpj(state.cliente.cpfCnpj);

          if (retornoConsultaCnpj !== undefined && UtilitarioGeral.valorValido(retornoConsultaCnpj.razaoSocial)) {
            state.cliente.nomeRazaoSocial = retornoConsultaCnpj.razaoSocial;
            state.cliente.email = retornoConsultaCnpj.contato.email;
            state.cliente.telefone = retornoConsultaCnpj.contato.telefonePrincipal;

            if (UtilitarioGeral.validaLista(state.cliente.enderecos)) {
              if (!UtilitarioGeral.valorValido(state.cliente.enderecos[0].endereco)) {
                state.cliente.enderecos[0].cep = retornoConsultaCnpj.endereco.cep;
                state.cliente.enderecos[0].numero = retornoConsultaCnpj.endereco.numero;
                state.cliente.enderecos[0].endereco = retornoConsultaCnpj.endereco.logadouro;
                state.cliente.enderecos[0].complemento = retornoConsultaCnpj.endereco.complemento;
                state.cliente.enderecos[0].bairro = retornoConsultaCnpj.endereco.bairro;
                state.cliente.enderecos[0].codigoCidade = retornoConsultaCnpj.endereco.codigoCidade;
              }
            } else {
              const endereco: IClienteEndereco = {} as IClienteEndereco;
              endereco.codigo = 0;
              endereco.principal = true;
              endereco.identificacao = 'PRINCIPAL';
              endereco.destinatario = '';
              endereco.cep = retornoConsultaCnpj.endereco.cep;
              endereco.numero = retornoConsultaCnpj.endereco.numero;
              endereco.endereco = retornoConsultaCnpj.endereco.logadouro;
              endereco.complemento = retornoConsultaCnpj.endereco.complemento;
              endereco.bairro = retornoConsultaCnpj.endereco.bairro;
              endereco.codigoCidade = retornoConsultaCnpj.endereco.codigoCidade;

              state.cliente.enderecos.push(endereco);
              state.chaveEndereco = (state.cliente.enderecos.length - 1).toString();
            }
          }
        }
      }
    }

    function abrirInformacoesCliente() {
      state.tela = 'informacoes';
    }

    function abrirUltimasVendas() {
      state.tela = 'ultimasVendas';
      state.abaUltimasVendas = 'vendas';
      state.pedidoSelecionadoIndex = -1;
    }

    function novoPedido() {
      if (state.cliente.codigo > 0) {
        router.push({
          name: 'ForcaVendasPedidoCliente',
          params: {
            codigoCliente: state.cliente.codigo,
          },
        });
      }
    }

    function abrirEdicao() {
      state.tela = 'editar';
    }

    function voltarTela() {
      if (state.tela === 'painel') {
        router.push({
          name: 'ForcaVendasClientes',
        });
        return;
      }

      if (state.tela === 'ultimasVendas' && state.abaUltimasVendas === 'itens') {
        state.abaUltimasVendas = 'vendas';
        return;
      }

      state.tela = 'painel';
    }

    function baixar() {
      window.print();
    }

    function selecionarPedido(index: number) {
      state.pedidoSelecionadoIndex = index;
      state.abaUltimasVendas = 'itens';
    }

    function somenteNumeros(valor: string) {
      if (!valor) return '';

      return valor.replace(/\D/g, '');
    }

    function formatarCpfCnpj(valor: string) {
      const numeros = somenteNumeros(valor);

      if (numeros.length === 11) {
        return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }

      if (numeros.length === 14) {
        return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }

      return valor || '-';
    }

    function formatarCep(valor: string) {
      const numeros = somenteNumeros(valor);

      if (numeros.length === 8) {
        return numeros.replace(/(\d{5})(\d{3})/, '$1-$2');
      }

      return valor || '';
    }

    function formatarMoeda(valor: number) {
      return (valor || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    function formatarMoedaCompacta(valor: number) {
      return (valor || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    function formatarQuantidade(valor: number) {
      return (valor || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    function formatarData(data: string) {
      if (!UtilitarioGeral.valorValido(data)) {
        return '-';
      }

      const dataConvertida = new Date(data);

      if (Number.isNaN(dataConvertida.getTime())) {
        return data;
      }

      return dataConvertida.toLocaleDateString('pt-BR');
    }

    function classeStatusPedido(pedido: IPedido) {
      return servicoPedido.obterCorStatus(pedido.status);
    }

    return {
      appBase,
      state,
      tituloTela,
      nomeFantasiaCliente,
      descricaoCanalCliente,
      codigoClienteFormatado,
      cpfCnpjFormatado,
      enderecoPrincipal,
      enderecoPrincipalCidade,
      pedidoSelecionado,
      itensUltimasVendas,
      totalItensUltimasVendas,
      salvar,
      consultaCep,
      consultaCnpj,
      adicionarEndereco,
      removerEndereco,
      abrirInformacoesCliente,
      abrirUltimasVendas,
      abrirEdicao,
      voltarTela,
      baixar,
      selecionarPedido,
      formatarCpfCnpj,
      formatarCep,
      formatarMoeda,
      formatarMoedaCompacta,
      formatarQuantidade,
      formatarData,
      classeStatusPedido,
      UtilitarioGeral,
      novoPedido,
      servicoPedido,
    };
  },
});
